import { useState, useEffect, useCallback, useRef } from "react";
import { useKalimbaSynth, getDefaultSettings, KalimbaSettings } from "./useKalimbaSynth";
import { TUNINGS, TuningType, TineDef } from "./tunings";
import { SONGS } from "./songs";
import { useMidi, midiNoteToFreq } from "../../hooks/useMidi";

export function RSKalimba() {
  const { playTine, updateSettings, init } = useKalimbaSynth();
  const [settings, setSettings] = useState<KalimbaSettings>(getDefaultSettings);
  const [activeTines, setActiveTines] = useState<Set<string>>(new Set());
  const [selectedTuning, setSelectedTuning] = useState<TuningType>("c-major");

  // Song Player State
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSongId, setCurrentSongId] = useState<string>("");
  const [labelMode, setLabelMode] = useState<"none" | "abc" | "jianpu">("none");
  const [showDemoMenu, setShowDemoMenu] = useState(false);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const tines = TUNINGS[selectedTuning].tines;

  useEffect(() => {
    setActiveTines(new Set());
  }, [selectedTuning]);

  const handleSettingChange = useCallback(
    (key: keyof KalimbaSettings, value: number) => {
      setSettings((prev) => {
        const next = { ...prev, [key]: value };
        updateSettings(next);
        return next;
      });
    },
    [updateSettings]
  );

  const [velocitySensitivity, setVelocitySensitivity] = useState(0.7);

  const triggerTine = useCallback(
    (id: string, freq: number, velocity = 127) => {
      init();
      const tine = tines.find((t) => t.id === id);
      if (!tine) return;

      const pan = (tine.index - 8) * 0.1;
      playTine(freq, pan, velocity);

      setActiveTines((prev) => new Set(prev).add(id));
      setTimeout(() => {
        setActiveTines((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, 200);
    },
    [playTine, init, tines]
  );

  // MIDI support - find closest tine by frequency
  const midi = useMidi({
    onNoteOn: (midiNote, velocity) => {
      const midiFreq = midiNoteToFreq(midiNote);
      // Find the closest tine by frequency
      let closestTine: TineDef | null = null;
      let closestDiff = Infinity;

      for (const tine of tines) {
        const diff = Math.abs(tine.freq - midiFreq);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestTine = tine;
        }
      }

      // Only trigger if the frequency is reasonably close (within 1 octave)
      if (closestTine && closestDiff < closestTine.freq) {
        const effectiveVelocity = Math.round(127 - (127 - velocity) * velocitySensitivity);
        triggerTine(closestTine.id, closestTine.freq, effectiveVelocity);
      }
    },
  });

  const stopSong = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setIsPlaying(false);
    setCurrentSongId("");
    setActiveTines(new Set());
  }, []);

  const playSong = useCallback((songId: string) => {
    const song = SONGS.find(s => s.id === songId);
    if (!song) return;

    stopSong();
    init();
    setSelectedTuning("c-major");
    setIsPlaying(true);
    setCurrentSongId(songId);

    let maxTime = 0;
    song.notes.forEach((n) => {
      const tuning = TUNINGS["c-major"].tines;
      const tine = tuning.find(t => t.id === n.note);
      if (tine) {
        const timeout = setTimeout(() => triggerTine(tine.id, tine.freq, n.velocity ?? 100), n.time * 1000);
        timeoutsRef.current.push(timeout);
        maxTime = Math.max(maxTime, n.time);
      }
    });

    const endTimeout = setTimeout(() => {
      setIsPlaying(false);
      setCurrentSongId("");
    }, (maxTime + 2) * 1000);
    timeoutsRef.current.push(endTimeout);
  }, [stopSong, init, triggerTine]);

  useEffect(() => {
    return () => stopSong();
  }, [stopSong]);

  useEffect(() => {
    const keyMap = new Map<string, (typeof tines)[number]>();
    tines.forEach(t => t.keys.forEach(k => keyMap.set(k, t)));
    const pressed = new Set<string>();

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (keyMap.has(key)) {
        e.preventDefault();
        e.stopPropagation();
        if (!pressed.has(key)) {
          pressed.add(key);
          const tine = keyMap.get(key)!;
          triggerTine(tine.id, tine.freq);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      pressed.delete(e.key.toLowerCase());
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [triggerTine, tines]);

  // Arrange tines in V-pattern
  const leftTines = tines.filter((t) => t.row === "left");
  const centerTines = tines.filter((t) => t.row === "center");
  const rightTines = tines.filter((t) => t.row === "right");

  return (
    <div className="max-w-sm mx-auto select-none">
      {/* Body - Warm wood-inspired but minimal */}
      <div className="bg-gradient-to-b from-[#3d3428] to-[#2a2318] rounded-[2.5rem] p-5 pt-6 shadow-[0_8px_32px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="w-24 flex justify-start">
            <button
              onClick={() => setLabelMode(mode => mode === "none" ? "abc" : mode === "abc" ? "jianpu" : "none")}
              className={`text-[8px] tracking-wider px-2 py-1 rounded transition-colors ${
                labelMode !== "none"
                  ? "bg-[#c9a86c] text-[#1a150f] font-semibold"
                  : "bg-[#1a150f] text-[#6a5a48] hover:text-[#8a7a60]"
              }`}
            >
              {labelMode === "jianpu" ? "123" : "ABC"}
            </button>
          </div>
          <div className="text-[10px] font-medium tracking-[0.3em] text-[#8a7a60] uppercase">RS-Kalimba</div>
          <div className="w-24 flex justify-end items-center gap-2">
            {midi.connected && (
              <input
                type="range"
                min="0"
                max="100"
                value={velocitySensitivity * 100}
                onChange={(e) => setVelocitySensitivity(Number(e.target.value) / 100)}
                className="w-12 h-1 accent-[#c9a86c]"
                title="Velocity sensitivity"
              />
            )}
            <div
              className={`text-[8px] tracking-wider px-2 py-1 rounded ${
                midi.connected
                  ? "bg-[#c9a86c] text-[#1a150f] font-semibold"
                  : "bg-[#1a150f] text-[#6a5a48]"
              }`}
              title={midi.connected ? (midi.deviceName || "MIDI Connected") : "MIDI not connected"}
            >
              MIDI
            </div>
          </div>
        </div>

        {/* Tine Assembly */}
        <div className="relative">
          {/* Comb/Bridge */}
          <div className="mx-auto w-[90%] h-2 bg-gradient-to-b from-[#8a8a8a] to-[#5a5a5a] rounded-sm shadow-[0_2px_4px_rgba(0,0,0,0.5)]" />

          {/* Tines */}
          <div className="flex justify-center items-start gap-0.5 -mt-1">
            {leftTines.map((tine, i) => (
              <Tine
                key={tine.id}
                tine={tine}
                isActive={activeTines.has(tine.id)}
                onClick={() => triggerTine(tine.id, tine.freq)}
                height={70 + i * 11}
                labelMode={labelMode}
              />
            ))}
            {centerTines.map((tine) => (
              <Tine
                key={tine.id}
                tine={tine}
                isActive={activeTines.has(tine.id)}
                onClick={() => triggerTine(tine.id, tine.freq)}
                height={158}
                isCenter
                labelMode={labelMode}
              />
            ))}
            {rightTines.map((tine, i) => (
              <Tine
                key={tine.id}
                tine={tine}
                isActive={activeTines.has(tine.id)}
                onClick={() => triggerTine(tine.id, tine.freq)}
                height={147 - i * 11}
                labelMode={labelMode}
              />
            ))}
          </div>
        </div>

        {/* Sound Hole */}
        <div className="flex justify-center mt-4">
          <div className="w-20 h-20 rounded-full bg-[#1a150f] shadow-[inset_0_4px_12px_rgba(0,0,0,0.8)]" />
        </div>

        {/* Controls */}
        <div className="mt-6 space-y-4">
          {/* Knobs */}
          <div className="flex justify-center gap-8">
            <Knob label="VOL" value={settings.volume} onChange={(v) => handleSettingChange("volume", v)} />
            <Knob label="DEC" value={settings.decay} onChange={(v) => handleSettingChange("decay", v)} />
            <Knob label="TONE" value={settings.tone} onChange={(v) => handleSettingChange("tone", v)} />
            <Knob label="REV" value={settings.reverb} onChange={(v) => handleSettingChange("reverb", v)} />
            <Knob label="BUZ" value={settings.buzz} onChange={(v) => handleSettingChange("buzz", v)} />
          </div>

          {/* Tuning Select - centered */}
          <div className="flex justify-center">
            <select
              value={selectedTuning}
              onChange={(e) => setSelectedTuning(e.target.value as TuningType)}
              disabled={isPlaying}
              className="bg-[#1a150f] text-[#8a7a60] text-[10px] tracking-wide border-none rounded px-3 py-1.5 focus:outline-none cursor-pointer"
            >
              {Object.entries(TUNINGS).map(([key, data]) => (
                <option key={key} value={key} className="bg-[#1a150f]">{data.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-3 text-[8px] text-[#555] tracking-wider">
        {/* Demo button with tooltip */}
        <div className="relative inline-block">
          {isPlaying ? (
            <button
              onClick={stopSong}
              className="text-[#c9a86c] hover:text-[#d4b87a]"
            >
              ■ Stop
            </button>
          ) : (
            <>
              <button
                onClick={() => setShowDemoMenu(!showDemoMenu)}
                className="hover:text-[#8a7a60]"
              >
                ▶ Demo
              </button>
              {showDemoMenu && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-[#2a2318] rounded-lg shadow-lg border border-[#3d3428] py-2 px-1 z-50">
                  {SONGS.map((song) => (
                    <button
                      key={song.id}
                      onClick={() => {
                        playSong(song.id);
                        setShowDemoMenu(false);
                      }}
                      className="block w-full text-left text-[9px] text-[#8a7a60] hover:text-[#c9a86c] hover:bg-[#1a150f] px-3 py-1.5 rounded whitespace-nowrap"
                    >
                      {song.title}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <span className="hidden md:inline"> · Q-P · A-L</span>
      </div>
    </div>
  );
}

// Convert note to jianpu: { num: 1-7, octave: -1/0/1 for low/mid/high }
function noteToJianpu(noteId: string): { num: number; octave: number } {
  const match = noteId.match(/^([A-G](?:b|#)?)(\d)$/);
  if (!match) return { num: 1, octave: 0 };

  const [, noteName, octaveStr] = match;
  const octave = parseInt(octaveStr, 10);

  // Map note name to scale degree (assuming C major / movable do)
  const noteMap: Record<string, number> = {
    'C': 1, 'D': 2, 'E': 3, 'F': 4, 'G': 5, 'A': 6, 'B': 7,
    'Db': 2, 'Eb': 3, 'Gb': 5, 'Ab': 6, 'Bb': 7, // flats shown as natural for simplicity
    'C#': 1, 'D#': 3, 'F#': 4, 'G#': 6, 'A#': 7, // sharps shown as natural
  };

  const num = noteMap[noteName] || 1;
  // Octave 5 is "middle", 4 is low (dot below), 6+ is high (dot above)
  const relativeOctave = octave - 5;

  return { num, octave: relativeOctave };
}

function Tine({ tine, isActive, onClick, height, isCenter = false, labelMode = "none" }: {
  tine: TineDef;
  isActive: boolean;
  onClick: () => void;
  height: number;
  isCenter?: boolean;
  labelMode?: "none" | "abc" | "jianpu";
}) {
  const isC = tine.id.startsWith("C");

  const renderLabel = () => {
    if (labelMode === "none") return null;

    if (labelMode === "abc") {
      return (
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[6px] font-bold text-[#555] whitespace-nowrap">
          {tine.id.replace(/(\d)/, '')}
        </div>
      );
    }

    // Jianpu mode
    const { num, octave } = noteToJianpu(tine.id);
    return (
      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex flex-col items-center text-[#555]">
        {octave > 0 && <div className="text-[4px] leading-none">•</div>}
        <div className="text-[7px] font-bold leading-none">{num}</div>
        {octave < 0 && <div className="text-[4px] leading-none">•</div>}
      </div>
    );
  };

  return (
    <div
      onPointerDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`relative transition-transform duration-50 cursor-pointer touch-none ${
        isActive ? "translate-y-1" : "hover:translate-y-0.5"
      }`}
      style={{
        width: isCenter ? '18px' : '14px',
        height: `${height}px`,
        background: isActive
          ? "linear-gradient(90deg, #d0d0d0 0%, #f8f8f8 30%, #e8e8e8 70%, #c0c0c0 100%)"
          : "linear-gradient(90deg, #909090 0%, #c8c8c8 30%, #b8b8b8 70%, #808080 100%)",
        borderRadius: '0 0 3px 3px',
        boxShadow: isActive
          ? '0 2px 4px rgba(0,0,0,0.3)'
          : '0 4px 8px rgba(0,0,0,0.4), inset 1px 0 0 rgba(255,255,255,0.3)'
      }}
    >
      {/* Dot marker for C notes */}
      {isC && (
        <div className={`absolute top-3 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${
          isCenter ? "bg-[#c9a86c]" : "bg-[#666]"
        }`} />
      )}
      {renderLabel()}
    </div>
  );
}

function Knob({ label, value, onChange }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const rotation = (value - 0.5) * 270;
  const dragRef = useRef<{ startY: number; startValue: number } | null>(null);

  const handleStart = (clientY: number) => {
    dragRef.current = { startY: clientY, startValue: value };
  };

  useEffect(() => {
    const handleMove = (clientY: number) => {
      if (!dragRef.current) return;
      const delta = (dragRef.current.startY - clientY) / 100;
      const newValue = Math.max(0, Math.min(1, dragRef.current.startValue + delta));
      onChange(newValue);
    };

    const handleEnd = () => {
      dragRef.current = null;
    };

    const onMouseMove = (e: MouseEvent) => handleMove(e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      if (dragRef.current) {
        e.preventDefault();
        handleMove(e.touches[0].clientY);
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", handleEnd);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [onChange]);

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className="w-8 h-8 rounded-full bg-gradient-to-b from-[#4a4035] to-[#2a2520] shadow-[0_2px_4px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.1)] cursor-ns-resize relative touch-none"
        onMouseDown={(e) => handleStart(e.clientY)}
        onTouchStart={(e) => {
          e.preventDefault();
          handleStart(e.touches[0].clientY);
        }}
      >
        <div
          className="absolute top-1.5 left-1/2 w-0.5 h-2.5 bg-[#c9a86c] rounded-full"
          style={{ transform: `translateX(-50%) rotate(${rotation}deg)`, transformOrigin: 'bottom center' }}
        />
      </div>
      <span className="text-[8px] text-[#6a5a48] tracking-wider">{label}</span>
    </div>
  );
}